import React, {useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

interface InfoTooltipProps {
  text: string;
  className?: string;
  position?: 'top' | 'right' | 'bottom' | 'left';
  children?: React.ReactNode;
}

const GAP_PX = 8;

function getPortalStyle(
  rect: DOMRect,
  position: 'top' | 'right' | 'bottom' | 'left',
): React.CSSProperties {
  switch (position) {
    case 'top':
      return {top: rect.top - GAP_PX, left: rect.left + rect.width / 2, transform: 'translate(-50%, -100%)'};
    case 'right':
      return {top: rect.top + rect.height / 2, left: rect.right + GAP_PX, transform: 'translateY(-50%)'};
    case 'left':
      return {top: rect.top + rect.height / 2, left: rect.left - GAP_PX, transform: 'translate(-100%, -50%)'};
    case 'bottom':
      return {top: rect.bottom + GAP_PX, left: rect.left + rect.width / 2, transform: 'translateX(-50%)'};
    default: {
      const _exhaustive: never = position;
      throw new Error(`Unknown tooltip position: ${_exhaustive}`);
    }
  }
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({text, className, position = 'bottom', children}) => {
  const [visible, setVisible] = useState(false);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) {
      setPortalStyle(null);
      return;
    }
    setPortalStyle(getPortalStyle(triggerRef.current.getBoundingClientRect(), position));
  }, [visible, position]);

  return (
    <span
      ref={triggerRef}
      className={`relative inline-block align-text-bottom ${className || ''}`}
      tabIndex={0}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-label={text}
      style={{outline: 'none'}}
    >
      {children ?? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-gray-400 cursor-pointer"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2" fill="white" />
          <rect x="9" y="8" width="2" height="5" rx="1" fill="currentColor" />
          <rect x="9" y="5" width="2" height="2" rx="1" fill="currentColor" />
        </svg>
      )}
      {visible && portalStyle && createPortal(
        <div
          role="tooltip"
          className="fixed z-[9999] whitespace-normal w-fit min-w-60 max-w-96 px-2 py-1.5 rounded bg-gray-200 border border-gray-300 text-gray-900 text-xs shadow-lg pointer-events-none"
          style={portalStyle}
        >
          {text}
        </div>,
        document.body,
      )}
    </span>
  );
};

export default InfoTooltip;
